import { useRef, useState } from "react";

import {
  faClose,
  faKeyboard,
  faSpinner,
  faWarning,
} from "@fortawesome/pro-regular-svg-icons";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { AnimatePresence } from "motion/react";
import { twJoin, twMerge } from "tailwind-merge";

import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { Input } from "src/base-components/Input";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { Pill } from "src/base-components/Pill";
import { ErrorHint } from "src/design-system/ErrorHint";
import { Icon } from "src/design-system/Icon";
import { Skeleton } from "src/design-system/Skeleton";
import type { HeadlessUIPlacement as Placement } from "src/utils/headlessui";

type TagsInputProps = {
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  placement?: Placement;
  errored?: boolean;
  dataLoc?: string;
  useLockNodeEditorClassName?: boolean;
  fullWidth?: boolean;
  maxItems?: number;
};

export const TagsInput: React.FC<TagsInputProps> = ({
  maxItems = Infinity,
  useLockNodeEditorClassName = false,
  ...props
}) => {
  const onChange = (value: string[]) => {
    props.onChange?.(value);

    selectRef.current?.dispatchEvent(new Event("change", { bubbles: true }));
  };

  const maxItemsNumberReached = props.value.length >= maxItems;

  const selectRef = useRef<HTMLSelectElement | null>(null);

  return (
    <>
      {/* Hidden native select element as workaround for
       * headless ui not using it and therefore not triggering
       * native events such as onChange in a form
       * https://github.com/tailwindlabs/headlessui/issues/2003
       */}
      <select ref={selectRef} style={{ display: "none" }} />
      <Popover>
        <div className={twJoin("flex", props.fullWidth && "w-full")}>
          <SelectButton
            {...props}
            maxItemsError={maxItemsNumberReached}
            onChange={onChange}
          />
        </div>
        <AnimatePresence initial={false}>
          <PopoverPanel
            anchor={{ to: props.placement ?? "bottom start", gap: 8 }}
            as={MotionDropdownPanel}
            className={twJoin(
              "rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-200 ring-opacity-5 focus:outline-none",
              "z-50 min-w-[var(--button-width)]",
              useLockNodeEditorClassName && LOCK_NODE_EDITOR_CLASSNAME,
            )}
            data-loc={`${props.dataLoc}-dropdown`}
          >
            <DefineItems
              maxItems={maxItems}
              maxItemsError={maxItemsNumberReached}
              value={props.value}
              onChange={onChange}
            />
          </PopoverPanel>
        </AnimatePresence>
      </Popover>
    </>
  );
};

const SelectButton: React.FC<
  TagsInputProps & {
    maxItemsError: boolean;
  }
> = (props) => {
  const className = twMerge(
    "flex min-h-[32px] w-full justify-between rounded-lg border border-gray-200 py-1 pr-2 pl-3",
    "focus:border-indigo-600 focus:outline-none focus:ring-2 focus:ring-indigo-600/25",
    "data-[open]:border-indigo-600 data-[open]:ring-2 data-[open]:ring-indigo-600/25",
    props.errored && "border-red-600 ring-2 ring-red-400/25",
    props.disabled ? "cursor-not-allowed bg-gray-50" : "bg-white",
  );

  const handleRemove = (removedOption: string) => {
    props.onChange?.(props.value.filter((v) => v !== removedOption));
  };

  const selectedValuesElement = (
    <div className="flex min-w-0 flex-1 flex-wrap gap-1">
      {props.value.map((option, index) =>
        option ? (
          <Pill key={option} size="sm" variant="gray">
            <Pill.Text>{option}</Pill.Text>
            {!props.disabled && (
              <div
                className="flex items-center opacity-60 hover:opacity-100"
                data-loc={`remove-tag-${option}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(option);
                }}
              >
                <Pill.Icon icon={faClose} />
              </div>
            )}
          </Pill>
        ) : (
          <Skeleton key={`skeleton-${index}`} height="h-5.5" width="w-24" />
        ),
      )}
    </div>
  );

  return (
    <PopoverButton
      className={twJoin("flex items-center gap-2.5", className)}
      data-loc={props.dataLoc}
      disabled={props.disabled}
      tabIndex={props.disabled ? -1 : 0}
    >
      {props.value.length > 0 ? (
        selectedValuesElement
      ) : (
        <span className="font-inter-normal-12px text-gray-500">
          {props.placeholder}
        </span>
      )}

      <div className="flex cursor-pointer">
        <Icon
          dataLoc="tags-input-icon"
          icon={props.loading ? faSpinner : faKeyboard}
          size="xs"
          spin={props.loading}
        />
      </div>
    </PopoverButton>
  );
};

const DefineItems: React.FC<
  Pick<TagsInputProps, "value" | "onChange" | "maxItems"> & {
    maxItemsError: boolean;
  }
> = (props) => {
  const [item, setItem] = useState("");
  const [isDirty, setIsDirty] = useState(false);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" && item && !props.value?.includes(item)) {
      if (props.maxItemsError) {
        setIsDirty(true);
        return;
      }

      props.onChange?.([...(props.value || []), item]);
      setItem("");
      setIsDirty(false);
    }
  };

  const isErrored = isDirty && props.maxItemsError;

  return (
    <div className="px-4 py-2">
      {isErrored && (
        <ErrorHint>Maximum {props.maxItems} items allowed</ErrorHint>
      )}
      <Input
        ref={(node) => node?.focus()} // Focus on first render
        data-loc="new-input"
        errored={isErrored}
        placeholder="Define new item and press Enter"
        suffixIcon={isErrored ? { icon: faWarning } : undefined}
        value={item}
        fullWidth
        onChange={(e) => setItem(e.target.value)}
        onKeyDown={handleKeyDown}
      />
    </div>
  );
};
